import React, { useCallback, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import { descargarDocumentoOrdenPago } from "./ApiServices";
import {
  formatMoneda,
  guardarBlobComoArchivo,
  obtenerCantidadOrdenesGeneradas,
  obtenerResumenGeneracion,
} from "./utils";

const nombreBancoOp = (item) => {
  const op = item?.orden_pago ?? item ?? {};
  return (
    item?.banco_nombre ??
    item?.banco ??
    op.banco_nombre ??
    item?.medio_pago?.banco_nombre ??
    "—"
  );
};

const ResultadoGeneracionMultiOp = ({ open, data, onClose }) => {
  const navigate = useNavigate();
  const [descargaActiva, setDescargaActiva] = useState(null);
  const [errorDescarga, setErrorDescarga] = useState(null);

  const ordenes = data?.ordenes_pago ?? [];
  const cantidad = obtenerCantidadOrdenesGeneradas(data);
  const notifGlobal = data?.notificacion_proveedor;
  const resumen = obtenerResumenGeneracion(data);

  const descargarInterbanking = useCallback(async (idOrden, clave) => {
    setDescargaActiva(clave);
    setErrorDescarga(null);
    try {
      const { blob, filename } = await descargarDocumentoOrdenPago(idOrden, {
        tipo: "interbanking",
      });
      guardarBlobComoArchivo(blob, filename);
    } catch (err) {
      setErrorDescarga(err.message || "Error al descargar Interbanking");
    } finally {
      setDescargaActiva(null);
    }
  }, []);

  const irADetalle = (id) => {
    onClose?.();
    navigate(`/ordenes_pago_proveedores/${id}`);
  };

  if (!open || !data) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Órdenes de pago generadas
        <IconButton
          aria-label="cerrar"
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="success" sx={{ mb: 2 }}>
          Se generaron {cantidad} órdenes de pago (una por banco origen).
          {notifGlobal?.mail_enviado
            ? ` Se envió un correo al proveedor con el detalle completo.`
            : notifGlobal?.motivo_no_envio
              ? ` Mail no enviado: ${notifGlobal.motivo_no_envio}`
              : ""}
        </Alert>

        {errorDescarga ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorDescarga(null)}>
            {errorDescarga}
          </Alert>
        ) : null}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#2471A3" }}>
                {["Banco origen", "N° OP", "Total neto", "Acciones"].map((h) => (
                  <TableCell
                    key={h}
                    align="center"
                    sx={{ color: "#fff", fontWeight: 500 }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {ordenes.map((item, index) => {
                const op = item.orden_pago ?? item;
                const id = op.id;
                const claveIb = `ib-${id ?? index}`;
                const tieneIb =
                  Boolean(item.archivo_interbanking) ||
                  op.tipo_bancario === "transferencia" ||
                  op.tipo_medio === "banco";

                return (
                  <TableRow key={id ?? index} hover>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600}>
                        {nombreBancoOp(item)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{op.nro_op ?? "—"}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      {formatMoneda(op.total_neto ?? item.resumen?.total_a_pagar)}
                    </TableCell>
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                        flexWrap="wrap"
                      >
                        {id ? (
                          <Tooltip title="Ver detalle">
                            <IconButton size="small" onClick={() => irADetalle(id)}>
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {tieneIb && id ? (
                          <Tooltip title="Descargar Interbanking">
                            <span>
                              <IconButton
                                size="small"
                                disabled={Boolean(descargaActiva)}
                                onClick={() => descargarInterbanking(id, claveIb)}
                              >
                                {descargaActiva === claveIb ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <DownloadIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {notifGlobal ? (
          <Alert
            severity={notifGlobal.mail_enviado ? "success" : "warning"}
            sx={{ mt: 2 }}
          >
            {notifGlobal.mail_enviado
              ? `Correo enviado a ${notifGlobal.destinatario || "el proveedor"} con totales y documentos (ZIP).`
              : `Correo no enviado${
                  notifGlobal.motivo_no_envio
                    ? `: ${notifGlobal.motivo_no_envio}`
                    : "."
                }`}
            {notifGlobal.nombre_adjunto ? (
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Adjunto: {notifGlobal.nombre_adjunto}
              </Typography>
            ) : null}
          </Alert>
        ) : null}

        {resumen ? (
          <Box sx={{ mt: 2, textAlign: "right" }}>
            <Typography variant="body2" color="text.secondary">
              Total a pagar
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary.main">
              {formatMoneda(resumen.total_a_pagar)}
            </Typography>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button
          variant="contained"
          onClick={() => {
            onClose?.();
            navigate("/ordenes_pago_proveedores");
          }}
        >
          Ir al listado
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResultadoGeneracionMultiOp;
